import { BarTrack, DetailSection, DetailShell, MetricRow } from './DetailShell'
import { SITE_STATUS_META } from '../../../lib/lens'
import { prettyName } from '../../../lib/mandal-name'
import { officerAccountability, type OfficerMandalGroup } from '../../../lib/officer-accountability'

import type { GisSite } from '../../../api/types'

interface OfficerDetailProps {
  officerId: string
  officerName: string
  /** The full sites payload — the drawer filters it by officerId client-side. */
  sites: GisSite[]
  onBack: () => void
  onSelectSite: (id: string) => void
}

const compColor = (pct: number): string => (pct < 40 ? '#dc2626' : pct < 70 ? '#ea580c' : '#16a34a')

/** One mandal's group of the officer's sites — each row status-coded, click → site detail. */
function MandalGroup({ group, onSelectSite }: { group: OfficerMandalGroup; onSelectSite: (id: string) => void }) {
  return (
    <DetailSection title={`${prettyName(group.mandal)} · ${group.surveyed}/${group.total} surveyed`}>
      {group.sites.map((s) => {
        const meta = SITE_STATUS_META[s.status]
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelectSite(s.id)}
            className="flex w-full items-center justify-between gap-2 border-b border-slate-100 py-2 text-left last:border-b-0 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-blue-600"
          >
            <span className="min-w-0 flex-1 truncate text-[12.5px]">{s.name}</span>
            <span className="inline-block flex-none rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${meta.color}22`, color: meta.color }}>
              {meta.label}
            </span>
          </button>
        )
      })}
    </DetailSection>
  )
}

/**
 * Officer accountability drawer (Phase 3): an officer's sites across the district, derived from the
 * bulk sites payload (`officer_id`, backend request #1) — overall site completion + the sites grouped
 * by mandal worst-first. Opened from any officer leaderboard row; each site drills to its detail.
 * The completion here is **site-survey** based (X of Y sites surveyed), distinct from the officer
 * report's survey-completion % on the leaderboard.
 */
export function OfficerDetail({ officerId, officerName, sites, onBack, onSelectSite }: OfficerDetailProps) {
  const a = officerAccountability(sites, officerId)
  return (
    <DetailShell
      backLabel="Officers"
      onBack={onBack}
      kicker="Officer"
      name={officerName}
      chip={{ text: `${a.completionPct}%`, bg: `${compColor(a.completionPct)}22`, color: compColor(a.completionPct) }}
    >
      <DetailSection title="Accountability">
        <MetricRow label="Assigned sites" value={`${a.total} · ${a.mandalCount} mandal${a.mandalCount === 1 ? '' : 's'}`} />
        <MetricRow label="Surveyed" value={`${a.surveyed} (${a.completionPct}%)`} />
        <BarTrack pct={a.completionPct} accent={compColor(a.completionPct)} />
        <MetricRow label="Overdue" value={`${a.overdue}`} />
        <MetricRow label="Never surveyed" value={`${a.never}`} />
        {a.inProgress > 0 && <MetricRow label="In progress" value={`${a.inProgress}`} />}
      </DetailSection>

      {a.total === 0 ? (
        <div className="text-muted-text py-6 text-center text-xs">No mapped sites assigned to this officer.</div>
      ) : (
        a.groups.map((g) => <MandalGroup key={g.mandal} group={g} onSelectSite={onSelectSite} />)
      )}
    </DetailShell>
  )
}
